import { FC } from 'react';

import { Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import IconInfo from '@/assets/icons/icon-info.svg?react';
import Table from '@/components/RTable';
import Tooltip from '@/components/RTooltip';
import useCurrency from '@/contexts/useCurrency';
import { SimulationResult } from '@/types/simulations';
import { formatNumberWithCommas } from '@/utils';

import { StyledTable } from './styles';

interface TableData {
  key: string;
  title: string | JSX.Element;
  energyAmount: string | number;
  cost?: number | string;
}

interface ExcessTableProps {
  simulationData: SimulationResult;
}

const ExcessTable: FC<ExcessTableProps> = ({ simulationData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { currencySymbol } = useCurrency();
  const dataTable: TableData[] = [
    {
      key: '1',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '10px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t('result.analysis.excessTable.curtailmentWithoutStorage')}
          </Typography.Text>
          <Tooltip
            title={t('result.analysis.excessTable.curtailmentWithoutStorage')}
            description={t(
              'result.analysis.excessTable.curtailmentWithoutStorageDescription'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      energyAmount: formatNumberWithCommas(
        Number(simulationData.result.curtailedEnergyWos.toFixed(0))
      ),
      cost: formatNumberWithCommas(
        Number(simulationData.result.curtailedEnergyCostWos.toFixed(0))
      ),
    },
    {
      key: '2',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '10px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t('result.analysis.excessTable.curtailmentWithStorage')}
          </Typography.Text>
          <Tooltip
            title={t('result.analysis.excessTable.curtailmentWithStorage')}
            description={t(
              'result.analysis.excessTable.curtailmentWithStorageDescription'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      energyAmount: formatNumberWithCommas(
        Number(simulationData.result.totalCurtailedEnergy.toFixed(0))
      ),
      cost: formatNumberWithCommas(
        Number(simulationData.result.curtailedEnergyCost.toFixed(0))
      ),
    },
    {
      key: '3',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '10px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t('result.analysis.excessTable.storageInefficiency')}
          </Typography.Text>
          <Tooltip
            title={t('result.analysis.excessTable.storageInefficiency')}
            description={t(
              'result.analysis.excessTable.storageInefficiencyDescription'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      energyAmount: formatNumberWithCommas(
        Number(simulationData.result.efficiencyLossInCurtailment.toFixed(0))
      ),
      cost: formatNumberWithCommas(
        Number(simulationData.result.efficiencyLossInCurtailmentCost.toFixed(0))
      ),
    },
    {
      key: '4',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '10px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t('result.analysis.excessTable.netEnergyExcessFromStorage')}
          </Typography.Text>
          <Tooltip
            title={t('result.analysis.excessTable.netEnergyExcessFromStorage')}
            description={t(
              'result.analysis.excessTable.netEnergyExcessFromStorageDescription'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      energyAmount: formatNumberWithCommas(
        Number(simulationData.result.netEnergyExcessFromStorage.toFixed(0))
      ),
      cost: formatNumberWithCommas(
        Number(
          simulationData.result.netEnergyExcessRevenueFromStorage.toFixed(0)
        )
      ),
    },
  ];

  const columns = [
    {
      dataIndex: 'title',
      width: '46%',
    },
    {
      title: t('result.analysis.excessTable.energyAmount'),
      dataIndex: 'energyAmount',
      key: 'energyAmount',
      width: '27%',
    },
    {
      title: t('result.analysis.excessTable.value', { symbol: currencySymbol }),
      dataIndex: 'cost',
      key: 'cost',
      width: '27%',
    },
  ];
  return (
    <StyledTable>
      <Table
        columns={columns}
        dataSource={dataTable}
        pagination={false}
        bordered
        size="small"
      />
    </StyledTable>
  );
};
export default ExcessTable;
